"use client";
import axios from "axios";
import { useState } from "react";
import toast from "react-hot-toast";
import axiosRetry from "axios-retry";
import { configStore } from "@/stores/config-store";

function useHttpV2() {
    const config = configStore();
    const [loading, setLoading] = useState(false);

    const http = axios.create({
        baseURL: process.env.NEXT_PUBLIC_BACKEND_URL,
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
        },
        withCredentials: true,
    });

    axiosRetry(http, {
        retries: 3,
        retryDelay: axiosRetry.linearDelay(),
        shouldResetTimeout: true,
        retryCondition: axiosRetry.isNetworkOrIdempotentRequestError,
    });

    const objToQueryParams = (obj) => {
        const params = new URLSearchParams();

        const appendParam = (key, value) => {
            if (Array.isArray(value)) {
                value.forEach((v) => appendParam(`${key}[]`, v));
            } else if (typeof value === "object" && value !== null) {
                Object.keys(value).forEach((subKey) => {
                    appendParam(`${key}[${subKey}]`, value[subKey]);
                });
            } else if (value !== undefined && value !== null) {
                params.append(key, value);
            }
        };

        Object.keys(obj || {}).forEach((key) => appendParam(key, obj[key]));

        return params.toString();
    };

    const handleConfigChecksum = (response) => {
        const checksum = response.headers["config-checksum"];
        if (checksum && checksum !== config.checksum) {
            config.refreshConfigByApi();
        }
    };

    const handleRedirect = (data) => {
        if (data.redirect) {
            if (typeof window === "undefined") {
                return
            }
            setTimeout(() => {
                window.open(data.redirect, "_self")
            }, 600);
        }
    };

    const safeResponse = (data = null, error = null, statusCode = null) => ({
        data,
        error,
        statusCode,
        hasError: !!error,
        errorMessage: error || null,
    });

    const post = async (path, data, forceFormData = false, showToast = true) => {
        setLoading(true);
        try {
            const response = await http.post(path, data, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                    "Content-Type": forceFormData
                        ? "multipart/form-data"
                        : "application/json",
                },
            });

            handleConfigChecksum(response);
            handleRedirect(response.data);

            if (response.data.message && showToast) toast.success(response.data.message);

            return safeResponse(response.data, null, response.status);
        } catch (err) {
            const statusCode = err?.response?.status || 500;
            const message =
                err?.response?.data?.message || "Ocorreu um erro desconhecido!";

            if (showToast) {
                if (statusCode === 401) toast.error("Não autorizado");
                else toast.error(message);
            }

            return safeResponse(null, message, statusCode);
        } finally {
            setLoading(false);
        }
    };

    const get = async (path, params = {}, showToast = true) => {
        setLoading(true);
        try {
            const query = objToQueryParams(params);
            const url = query ? `${path}?${query}` : path;

            const response = await http.get(url, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });

            handleConfigChecksum(response);
            if (response.data.message && showToast) toast.success(response.data.message);

            return safeResponse(response.data, null, response.status);
        } catch (err) {
            const statusCode = err?.response?.status || 500;
            const message =
                err?.response?.data?.message || "Ocorreu um erro desconhecido!";

            if (showToast) toast.error(message);

            return safeResponse(null, message, statusCode);
        } finally {
            setLoading(false);
        }
    };

    return { get, post, loading };
}

export default useHttpV2;
